/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import { findMatchesInText, sanitizeForSearch } from "@/lib/process-package";
import type {
  ProcessedChannel,
  SearchFilters,
  SearchResult,
  SearchStats,
} from "@/types";
import MiniSearch from "minisearch";
import { create } from "zustand";

interface SearchState {
  filters: SearchFilters;
  results: SearchResult[];
  stats: SearchStats | null;
  isSearching: boolean;

  searchIndex: MiniSearch | null;
  isIndexing: boolean;
  indexStats: {
    messageCount: number;
    lastUpdated: Date;
  } | null;

  setFilters: (filters: Partial<SearchFilters>) => void;
  clearFilters: () => void;
  clearFilter: (key: keyof SearchFilters) => void;
  search: (channels?: ProcessedChannel[]) => void;
  buildIndex: (channels: ProcessedChannel[]) => Promise<void>;
  reset: () => void;
}

const initialFilters: SearchFilters = {
  terms: "",
  dateRange: undefined,
  channelTypes: [],
  serverNames: [],
  hasAttachments: false,
  showLeftServers: false,
};

const SEARCH_OPTIONS = {
  fields: ["content", "channelName", "serverName"] as string[],
  storeFields: [
    "id",
    "channelId",
    "content",
    "timestamp",
    "attachments",
    "channelType",
    "serverName",
    "channelName",
  ] as string[],
  searchOptions: {
    boost: { content: 2, channelName: 1, serverName: 1 },
    fuzzy: 0.2,
    prefix: true,
  },
} as const;

function extractSearchQuery(raw?: string) {
  if (!raw) {
    return { normalized: "", exactPhrases: [] as string[] };
  }

  const phrasePattern = /"([^"]+)"/g;
  const phrases: string[] = [];
  const cleaned = raw.replace(phrasePattern, (_match, phrase) => {
    phrases.push(phrase);
    return " ";
  });

  return {
    normalized: sanitizeForSearch(cleaned).trim(),
    exactPhrases: phrases
      .map((phrase) => sanitizeForSearch(phrase))
      .filter(Boolean),
  };
}

export const useSearch = create<SearchState>()((set, get) => ({
  filters: initialFilters,
  results: [],
  stats: null,
  isSearching: false,
  searchIndex: null,
  isIndexing: false,
  indexStats: null,

  setFilters: (newFilters) => {
    set((state) => ({
      filters: {
        ...state.filters,
        ...newFilters,
      },
    }));

    if (get().searchIndex) {
      get().search();
    }
  },

  clearFilters: () => {
    set({ filters: initialFilters });
    get().search();
  },

  clearFilter: (key) => {
    set((state) => ({
      filters: {
        ...state.filters,
        [key]:
          key === "terms"
            ? ""
            : key === "hasAttachments"
              ? false
              : key === "showLeftServers"
                ? false
                : Array.isArray(state.filters[key])
                  ? []
                  : undefined,
      },
    }));
    get().search();
  },

  buildIndex: async (channels: ProcessedChannel[]) => {
    set({ isIndexing: true });
    const CHUNK_SIZE = 5000;

    try {
      const searchIndex = new MiniSearch({
        ...SEARCH_OPTIONS,
        tokenize: (string: string) => sanitizeForSearch(string).split(/\s+/),
      });

      let totalDocuments = 0;

      for (let i = 0; i < channels.length; i += CHUNK_SIZE) {
        const channelChunk = channels.slice(i, i + CHUNK_SIZE);

        const documents = channelChunk.flatMap((channel) =>
          channel.messages.map((message) => ({
            id: message.ID,
            channelId: channel.id,
            content: message.Contents,
            timestamp: message.Timestamp,
            attachments: message.parsedAttachments?.map((a) => a.url) || [],
            channelType: channel.type,
            serverName: channel.serverName || "",
            channelName: channel.displayName,
            searchableText: sanitizeForSearch(
              [
                message.Contents,
                channel.displayName,
                channel.serverName,
                channel.channelCategory,
              ]
                .filter(Boolean)
                .join(" "),
            ),
          })),
        );

        await searchIndex.addAllAsync(documents);
        totalDocuments += documents.length;

        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      set({
        searchIndex,
        isIndexing: false,
        indexStats: {
          messageCount: totalDocuments,
          lastUpdated: new Date(),
        },
      });

      get().search();
    } catch (error) {
      console.error("Error building search index:", error);
      set({
        isIndexing: false,
        indexStats: null,
        searchIndex: null,
      });
    }
  },

  search: (channels?: ProcessedChannel[]) => {
    if (channels) {
      get().buildIndex(channels);
      return;
    }

    const { searchIndex, filters } = get();
    if (!searchIndex) return;

    set({ isSearching: true });
    const CHUNK_SIZE = 1000;

    try {
      console.log("Search filters:", JSON.stringify(filters, null, 2));
      console.log("Left servers filter active:", filters.showLeftServers);

      const { normalized, exactPhrases } = extractSearchQuery(filters.terms);
      const searchInput = normalized || exactPhrases.join(" ");

      const rawSearchResults =
        searchInput.length > 0
          ? searchIndex.search(searchInput)
          : searchIndex.search("*");

      const sanitizedContentCache = new Map<string, string>();
      const searchResults =
        exactPhrases.length > 0
          ? rawSearchResults.filter((result) => {
              const content = result.content || "";
              let sanitizedContent = sanitizedContentCache.get(content);
              if (!sanitizedContent) {
                sanitizedContent = sanitizeForSearch(content);
                sanitizedContentCache.set(content, sanitizedContent);
              }
              return exactPhrases.every((phrase) =>
                sanitizedContent.includes(phrase),
              );
            })
          : rawSearchResults;

      const validChannelTypes = new Set(filters.channelTypes);
      const validServerNames = new Set(filters.serverNames);
      const dateFrom = filters.dateRange?.from?.getTime();
      const dateTo = filters.dateRange?.to?.getTime();
      const termMatchesCache = new Map<string, [number, number][]>();

      const processedResults: SearchResult[] = [];

      let totalResults = 0;
      let leftServerResults = 0;
      let nonLeftServerResults = 0;

      for (let i = 0; i < searchResults.length; i += CHUNK_SIZE) {
        const resultChunk = searchResults.slice(i, i + CHUNK_SIZE);

        for (const result of resultChunk) {
          totalResults++;

          const isGuildChannel = result.channelType.startsWith("GUILD_");
          const isLeftServer = isGuildChannel && !result.serverName;

          if (totalResults < 20) {
            console.log(
              `Result ${totalResults}: Type=${result.channelType}, ` +
                `ServerName=${result.serverName || "none"}, ` +
                `isGuildChannel=${isGuildChannel}, ` +
                `isLeftServer=${isLeftServer}`,
            );
          }

          if (
            filters.channelTypes.length > 0 &&
            !validChannelTypes.has(result.channelType)
          ) {
            continue;
          }

          if (
            filters.serverNames.length > 0 &&
            !validServerNames.has(result.serverName)
          ) {
            continue;
          }

          if (
            filters.hasAttachments &&
            (!result.attachments || result.attachments.length === 0)
          ) {
            continue;
          }

          if (filters.showLeftServers && !isLeftServer) {
            if (totalResults < 20) {
              console.log(`  -> Skipping because not a left server`);
            }
            nonLeftServerResults++;
            continue;
          }

          if (isLeftServer) {
            leftServerResults++;
            if (totalResults < 20) {
              console.log(`  -> Is a left server!`);
            }
          }

          if (dateFrom || dateTo) {
            const timestamp = new Date(result.timestamp).getTime();
            if (
              (dateFrom && timestamp < dateFrom) ||
              (dateTo && timestamp > dateTo)
            ) {
              continue;
            }
          }

          let matches: [number, number][] = [];
          if (filters.terms && result.content) {
            if (!termMatchesCache.has(result.content)) {
              termMatchesCache.set(
                result.content,
                findMatchesInText(result.content, filters.terms),
              );
            }
            matches = termMatchesCache.get(result.content)!;
          }

          processedResults.push({
            messageId: result.id,
            channelId: result.channelId,
            content: result.content,
            timestamp: new Date(result.timestamp),
            matches,
            attachments: result.attachments,
            channelName: result.channelName,
            serverName: result.serverName,
            channelType: result.channelType,
            score: result.score,
          });
        }
      }

      const stats: SearchStats = {
        totalResults: searchResults.length,
        filteredResults: processedResults.length,
        channelCount: new Set(processedResults.map((r) => r.channelId)).size,
        serverCount: new Set(processedResults.map((r) => r.serverName)).size,
        attachmentCount: processedResults.reduce(
          (sum, r) => sum + (r.attachments?.length || 0),
          0,
        ),
        typeCounts: processedResults.reduce(
          (acc, result) => {
            acc[result.channelType] = (acc[result.channelType] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        ),
        serverCounts: processedResults.reduce(
          (acc, result) => {
            acc[result.serverName] = (acc[result.serverName] || 0) + 1;
            return acc;
          },
          {} as Record<string, number>,
        ),
      };

      console.log(`Search results stats:
        Total processed: ${totalResults}
        Left server results: ${leftServerResults}
        Non-left server results: ${nonLeftServerResults}
        Final filtered results: ${processedResults.length}
        Left servers filter active: ${filters.showLeftServers}
      `);

      set({
        results: processedResults,
        stats,
        isSearching: false,
      });
    } catch (error) {
      console.error("Search error:", error);
      set({
        results: [],
        stats: null,
        isSearching: false,
      });
    }
  },

  reset: () => {
    set({
      filters: initialFilters,
      results: [],
      stats: null,
      isSearching: false,
    });
  },
}));
